/**
 * English glossary and lesson copy, mirroring `ID_CONTENT` entry for entry.
 *
 * Same rule as the Indonesian text: assume no prior Mahjong knowledge and
 * explain each term the first time it appears.
 */

import type { LocaleContent } from "./content.js";

export const EN_CONTENT: LocaleContent = {
  glossary: [
    { id: "tile", term: "Tile", meaning: "A single Mahjong piece. One set contains 136 tiles." },
    { id: "hand", term: "Hand", meaning: "The tiles you hold. Only you can see them." },
    { id: "wall", term: "Wall", meaning: "The stack of tiles nobody has drawn yet. If it runs out, the round is a draw." },
    { id: "draw", term: "Draw", meaning: "Taking one tile from the wall. Every turn starts with this." },
    { id: "discard", term: "Discard", meaning: "Putting one tile from your hand on the table, where everyone can see it." },
    { id: "pair", term: "Pair", meaning: "Two identical tiles. A winning hand needs exactly one pair." },
    { id: "chow", term: "Chow", meaning: "Three consecutive tiles of the same suit, for example 3-4-5 Bamboo. You may only claim one from the player on your left." },
    { id: "pong", term: "Pong", meaning: "Three identical tiles. You may claim one from any player's discard." },
    { id: "kong", term: "Kong", meaning: "Four identical tiles. After a Kong you draw one replacement tile." },
    { id: "meld", term: "Meld", meaning: "A completed group of three tiles: a chow or a pong. A winning hand needs 4 of them." },
    { id: "self-draw", term: "Self-draw", meaning: "Winning with a tile you drew yourself from the wall. Often called tsumo." },
    { id: "win-from-discard", term: "Win from a discard", meaning: "Winning with a tile another player has just discarded. Often called ron." },
    { id: "dealer", term: "Dealer", meaning: "The player who starts the round. The dealer holds 14 tiles and discards one straight away." },
    { id: "seat-wind", term: "Seat wind", meaning: "The direction of your seat: East, South, West or North. Turns follow that order." },
  ],
  lessons: {
    dasar: {
      title: "1. Mahjong Basics",
      objective: "Learn the goal of the game and what a Mahjong set contains.",
      intro: [
        "Mahjong is played by 4 people with 136 tiles.",
        "The basic goal is to form 4 sets and 1 pair.",
        "A set is three tiles: three identical tiles (a Pong) or three consecutive tiles of the same suit (a Chow). A pair is two identical tiles.",
        "There are three numbered suits from 1 to 9 — Characters, Bamboo and Dots — plus 4 Winds and 3 Dragons.",
        "You are the dealer and hold 14 tiles, so your first move is to discard one tile.",
      ],
      goal: "Discard one tile from your hand.",
      coach: "Look for the loneliest tile: the one with no pair and no neighbours. That tile has the fewest ways to become a set.",
      success: "Well done. That is one full turn: draw a tile, then discard one.",
    },
    "ambil-buang": {
      title: "2. Draw & Discard",
      objective: "Get used to the rhythm of every turn: draw one tile, discard one tile.",
      intro: [
        "Every turn is the same: draw one tile from the wall, then discard one tile.",
        "That is why your hand always comes back to 13 tiles.",
        "The tile you just drew is shown separately so it is easy to spot.",
        "Turns move in one direction: you, then South, West, North.",
      ],
      goal: "Draw one tile from the wall, then discard one tile.",
      coach: "Press Draw tile first. Once the new tile arrives, pick the least useful tile and discard it.",
      success: "That is the basic rhythm of Mahjong. Next we look at claiming another player's discard.",
    },
    chow: {
      title: "3. Chow",
      objective: "Complete a run using a tile discarded by the player on your left.",
      intro: [
        "A Chow is three consecutive tiles of the same suit, for example 3-4-5 Bamboo.",
        "You may claim a discard to complete a run, but only from the player on your left — the one who moves right before you.",
        "You hold 3 and 4 Bamboo. The North player discards 5 Bamboo, and that completes 3-4-5.",
        "After a Chow the set is exposed on the table and you immediately discard one tile.",
      ],
      goal: "Claim the discarded 5 Bamboo to form the run 3-4-5.",
      coach: "Press the Chow button in the claim panel. The finished set will appear face up under your hand.",
      success: "Chow complete. Notice the exposed set under your hand.",
    },
    pong: {
      title: "4. Pong",
      objective: "Complete three identical tiles from any player's discard.",
      intro: [
        "A Pong is three identical tiles.",
        "Unlike a Chow, a Pong may be claimed from any player at the table.",
        "You hold two Red Dragons, and the South player has just discarded the third one.",
        "If two players claim the same tile, a Pong beats a Chow.",
      ],
      goal: "Claim the discarded Red Dragon to form three of a kind.",
      coach: "Press the Pong button. You still have to discard one tile afterwards, because a claim does not add a turn.",
      success: "Pong complete. Your three Red Dragons are now exposed on the table.",
    },
    kong: {
      title: "5. Kong",
      objective: "Form four identical tiles and understand the replacement draw.",
      intro: [
        "A Kong is four identical tiles.",
        "You can form one from another player's discard, or from your own hand once you hold all four.",
        "A Kong still counts as a single set.",
        "That is why you immediately draw one replacement tile from the dead wall, so your hand keeps the right number of tiles.",
      ],
      goal: "Claim the fourth West Wind to form a Kong.",
      coach: "Press the Kong button. The replacement tile arrives in your hand automatically, then discard one tile.",
      success: "Kong complete, and your replacement tile has arrived. Now discard one tile.",
    },
    "menang-sendiri": {
      title: "6. Winning by Self-Draw",
      objective: "Close your hand into 4 sets + 1 pair with a tile from the wall.",
      intro: [
        "Your hand needs just one more Red Dragon to become 4 sets and 1 pair.",
        "The next tile in the wall is a Red Dragon.",
        "Draw it, then press the Win button.",
        "Winning with a tile you drew yourself is called a self-draw, or tsumo.",
      ],
      goal: "Draw a tile from the wall, then declare the win.",
      coach: "Press Draw tile first. As soon as the Red Dragon arrives, the Win button becomes active.",
      success: "Congratulations, that is your first win!",
    },
    "menang-dari-buangan": {
      title: "7. Winning from a Discard",
      objective: "Close your hand with a tile another player has just discarded.",
      intro: [
        "You can also win on a tile another player discards. That is called a ron.",
        "The West player has just discarded the Red Dragon you were waiting for.",
        "A winning claim beats every other claim, including Pong and Chow.",
      ],
      goal: "Claim the discarded Red Dragon to win.",
      coach: "Press the Win! button in the claim panel, not Pass. That discard completes your hand.",
      success: "Perfect. You now know both ways to win: from the wall and from a discard.",
    },
  },
};
